import { PhoneNumberAnywhere, UserCallControl } from '../../app/types'
import { FormSection } from '../../common/components/SectionedForm'
import { TFunction, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectedSiteSelector } from '../../authentication/redux'
import React, { ReactElement, ReactNode, useCallback } from 'react'
import { api } from '../../common/api'
import { useFetchData } from '../../common/servercall/useFetchData'
import { LabelValues, LabelValueType } from '../../common/components/LabelValues'
import PaperStack from '../../common/form/PaperStack'
import FormGroup from '../../common/form/FormGroup'
import { Check, SettingsPhone } from '@mui/icons-material'
import { Box, Card, CardContent, CardHeader, Grid, List, ListItem, ListItemText, Paper, Table, TableContainer, Typography } from '@mui/material'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import { CallControlForm } from './callControlForm'
import { useFormSubmissionButtons } from '../../common/form/useFormSubmissmionButtons'

const activeNotActive = (active: boolean, t: TFunction) : string => t(`users.edit.${!active? 'not-' : ''}active`)

export const useCallControlSection = (userId: string, enabled: boolean) : FormSection<UserCallControl> => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const {buildFormButtons} = useFormSubmissionButtons(false)

    const fetchUserCallControl = useCallback(
        async (userId: string): Promise<UserCallControl | undefined> => {
            if (!site || !enabled) return
            const { data } = await api.get<UserCallControl>(`/api/site/${site.productId}/user/${userId}/call-control-settings`)
            return data
        },
        [site, enabled]
    )
    const { data, isFetching, error, refetch } = useFetchData<UserCallControl>(useCallback(() => fetchUserCallControl(userId), [fetchUserCallControl, userId]))

    const onSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: UserCallControl) => {
                if (!site) return
                await api.put(`/api/site/${site.productId}/user/${userId}/call-control-settings`, formData)
                await refetch()
                onSucceeded();
            },
        [site, userId, refetch],
    )

    const queueAvailibiltyLabelValues = useCallback(
        (value: UserCallControl): LabelValueType[] => {
            return [
                {
                    label: '',
                    value: (value.callCenterAvailabilities && value.callCenterAvailabilities.length > 0) ? (
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table sx={{ minWidth: 300 }} size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 700 }}>{t('flows.table.name')}</TableCell>
                                        <TableCell style={{ fontWeight: 700 }} align="right">
                                            {t('flows.detail.joined')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {value.callCenterAvailabilities.map(it => (
                                        <TableRow key={it.callCenterId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell component='th' scope='row'>
                                                {it.name}
                                            </TableCell>
                                            <TableCell align='right'>
                                                <Box alignSelf={'center'} sx={{ marginLeft: 1 }}>
                                                    {it.available ? <Check color={'success'} /> : <></>}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : <Typography>{t('users.edit.agent-settings.no-queues-joined')}</Typography>,
                    type: 'NORMAL',
                },
            ]}, [t]
    )

    const officeAnywhereLabelValues = useCallback(
        (value: UserCallControl): LabelValueType[] => {
            return [
                {   label: t('users.edit.office-anywhere.alert-all-locations'),
                    value: <>{activeNotActive(value.alertAllLocations, t)}</>
                },
                {   label: t('users.edit.sequential-ring.ring-numbers'),
                    value: <List dense={true}>
                        {value.phoneNumberAnywhereList &&
                            value.phoneNumberAnywhereList.filter(it => it.active).map(anywhereNumber => {
                                return <ListItem key={anywhereNumber.number}>
                                    <ListItemText primary={anywhereNumber.number} secondary={constructList(anywhereNumber).map(it => t(it)).join(", ")}/>
                                </ListItem>
                            })}
                    </List>,
                    type: 'FULL_LINE',
                },
            ]}, [t]
    )

    const guardTimerLabelValues = useCallback(
        (value: UserCallControl): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.use-system-default'),
                value: <>{value.useSystemDefaultGuardTimer ? t('general.yes') : t('general.no')}</>
            },
            ...(value.useSystemDefaultGuardTimer ? [] : [
                {
                    label: t('settings.call-center-agent.guard-timer'),
                    value: <>{value.enableGuardTimer ? t('settings.call-center-agent.guard-timer-seconds-value', { seconds: value.guardTimerSeconds }) : t('general.no')}</>
                }
            ])
        ], [t])

    const unavailabilityLabelValues = useCallback(
        (value: UserCallControl): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.use-system-default'),
                value: <>{value.useSystemDefaultUnavailableSettings ? t('general.yes') : t('general.no')}</>
            },
            ...(value.useSystemDefaultUnavailableSettings ? [] : [
                {
                    label: t('settings.call-center-agent.force-au-on-dnd-activation'),
                    value: <>{value.forceAgentUnavailableOnDNDActivation ? t('general.yes') : t('general.no')}</>
                },
                {
                    label: t('settings.call-center-agent.force-au-on-personal-calls'),
                    value: <>{value.forceAgentUnavailableOnPersonalCalls ? t('general.yes') : t('general.no')}</>
                },
                {
                    label: t('settings.call-center-agent.force-au-on-bounced-call-limit'),
                    value: <>{value.forceAgentUnavailableOnBouncedCallLimit ?
                        t('settings.call-center-agent.bounces-nr-of', { bounces: value.numberConsecutiveBouncedCallsToForceAgentUnavailable }) : t('general.no')}</>
                },
                {
                    label: t('settings.call-center-agent.force-au-on-not-reachable'),
                    value: <>{value.forceAgentUnavailableOnNotReachable ? t('general.yes') : t('general.no')}</>
                }
            ])
        ], [t])

    const wrapUpLabelValues = useCallback(
        (value: UserCallControl): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.use-system-default'),
                value: <>{value.useSystemDefaultWrapUpDestination ? t('general.yes') : t('general.no')}</>
            },
            ...(value.useSystemDefaultWrapUpDestination ? [] : [
                {
                    label: t('settings.call-center-agent.wrap-up-destination'),
                    value: <>{value.wrapUpDestination ? value.wrapUpDestination : t('general.none')}</>
                }
            ])
        ], [t])

    const renderAgentSettings = useCallback(
        (value: UserCallControl): ReactNode => (
            <>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader subheader={t('users.edit.agent-settings.acd-state')} />
                        <CardContent>
                            <Grid container rowSpacing={4} p={1}>
                                <LabelValues labelValues={[{ label: t('users.edit.agent-settings.acd-state-label'), value: <>{value.agentACDState}</> }]} />
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader subheader={t('settings.call-center-agent.guard-timer')} />
                        <CardContent>
                            <Grid container rowSpacing={4} p={1}>
                                <LabelValues labelValues={guardTimerLabelValues(value)} />
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader subheader={t('settings.call-center-agent.unavailability-settings')} />
                        <CardContent>
                            <Grid container rowSpacing={4} p={1}>
                                <LabelValues labelValues={unavailabilityLabelValues(value)} />
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader subheader={t('settings.call-center-agent.wrap-up-settings')} />
                        <CardContent>
                            <Grid container rowSpacing={4} p={1}>
                                <LabelValues labelValues={wrapUpLabelValues(value)} />
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </>
        ), [t, guardTimerLabelValues, unavailabilityLabelValues, wrapUpLabelValues])

    const renderCallControlDetails = useCallback(
        (value: UserCallControl): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup key={1} label={t('users.edit.queue-availability.title')} fullWidth={true}>
                        <LabelValues labelValues={queueAvailibiltyLabelValues(value)} />
                    </FormGroup>
                    <FormGroup key={2} label={t('users.edit.office-anywhere.title')} fullWidth={true}>
                        <LabelValues labelValues={officeAnywhereLabelValues(value)} />
                    </FormGroup>
                    <FormGroup key={3} label={t('users.edit.agent-settings.title')} fullWidth={true}>
                        {value.callCenterAvailabilities && value.callCenterAvailabilities.length > 0
                            ? renderAgentSettings(value)
                            : <Grid item xs={12}><Typography>{t('users.edit.agent-settings.no-queues-joined')}</Typography></Grid>}
                    </FormGroup>
                </PaperStack>
            )
        },
        [t, queueAvailibiltyLabelValues, officeAnywhereLabelValues, renderAgentSettings]
    )

    const renderForm = useCallback((callControl: UserCallControl, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
            <CallControlForm callControl={callControl} buildFormButtons={buildFormButtons(cancelEditButton)} onSubmit={onSubmit(onSucceeded)} />,
        [onSubmit, buildFormButtons])

    return {
        title: t('users.sections.call-control'),
        data: data,
        isFetching: isFetching,
        error: error,
        refetch: refetch,
        renderDetails: renderCallControlDetails,
        renderForm: renderForm,
        enabled: () => enabled,
        icon: () => <SettingsPhone />
    }
}

const constructList = (numberAnywhere: PhoneNumberAnywhere) => {
    let resultList = []
    if (numberAnywhere.useDiversionInhibitor) {
        resultList.push('users.edit.office-anywhere.do-not-forward')
    }
    if (numberAnywhere.answerConfirmationRequired) {
        resultList.push('users.edit.office-anywhere.answer-confirmation')
    }
    if (numberAnywhere.callControl) {
        resultList.push('users.edit.office-anywhere.call-control')
    }
    return resultList
}